/**
 * 현장 QA 한 항목의 진행 결과 기록 다이얼로그.
 *
 * 규칙은 백엔드와 같다 — 실패면 사유 필수, 성공인데 날짜를 비우면 오늘,
 * QA 불가면 불가 사유 필수이고 결과는 미진행으로 둔다.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { QA_RESULT_LABEL, QaResult, SiteQaItem, SiteQaItemUpdate } from '../../services/SiteQaService';
import { getUserInfo } from '../../utils/auth';

interface Props {
  item: SiteQaItem | null;
  /** 목록의 [실패] 처럼 결과를 미리 고른 채 열 때 */
  preset?: { result?: QaResult; applicable?: boolean };
  onClose: () => void;
  onSubmit: (id: number, value: SiteQaItemUpdate) => Promise<void>;
}

interface FormState {
  applicable: boolean;
  unavailable_reason: string;
  result: QaResult;
  fail_reason: string;
  passed_at: string;
  assignee: string;
  note: string;
}

function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const QaResultDialog: React.FC<Props> = ({ item, preset, onClose, onSubmit }) => {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    const applicable = preset?.applicable ?? item.applicable;
    const result = preset?.result ?? item.result;
    setForm({
      applicable,
      unavailable_reason: item.unavailable_reason || '',
      result,
      fail_reason: item.fail_reason || '',
      passed_at: item.passed_at || (result === 'pass' ? today() : ''),
      // 담당자가 비어 있으면 기록하는 사람으로 채워 둔다 — 고칠 수 있다.
      assignee: item.assignee || getUserInfo()?.name || '',
      note: item.note || '',
    });
    setError(null);
  }, [item, preset]);

  if (!item || !form) return null;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const submit = async () => {
    if (!form.applicable && !form.unavailable_reason.trim()) {
      setError('QA 불가 사유를 입력하세요');
      return;
    }
    if (form.applicable && form.result === 'fail' && !form.fail_reason.trim()) {
      setError('실패 사유를 입력하세요');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(item.id, {
        applicable: form.applicable,
        unavailable_reason: form.applicable ? '' : form.unavailable_reason.trim(),
        result: form.applicable ? form.result : 'pending',
        fail_reason: form.fail_reason.trim(),
        passed_at: form.applicable && form.result === 'pass' ? form.passed_at : '',
        assignee: form.assignee.trim(),
        note: form.note.trim(),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        QA 결과 기록
        <Typography variant="body2" color="text.secondary">
          {item.category ? `[${item.category}] ` : ''}
          {item.title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {item.method && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.method}
            </Typography>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              결과
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.applicable ? form.result : 'na'}
              onChange={(_, v: QaResult | 'na' | null) => {
                if (!v) return;
                if (v === 'na') {
                  set('applicable', false);
                  return;
                }
                set('applicable', true);
                set('result', v);
                if (v === 'pass' && !form.passed_at) set('passed_at', today());
              }}
            >
              {(['pending', 'pass', 'fail'] as QaResult[]).map((r) => (
                <ToggleButton
                  key={r}
                  value={r}
                  color={r === 'pass' ? 'success' : r === 'fail' ? 'error' : 'standard'}
                  data-testid={`qa-result-${r}`}
                  sx={{ px: 2.5 }}
                >
                  {QA_RESULT_LABEL[r]}
                </ToggleButton>
              ))}
              <ToggleButton value="na" color="warning" data-testid="qa-result-na" sx={{ px: 2.5 }}>
                QA 불가
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {!form.applicable && (
            <TextField
              label="불가 사유"
              value={form.unavailable_reason}
              onChange={(e) => set('unavailable_reason', e.target.value)}
              required
              multiline
              minRows={2}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ 'data-testid': 'qa-unavailable-reason' }}
            />
          )}
          {form.applicable && form.result === 'fail' && (
            <TextField
              label="실패 사유"
              value={form.fail_reason}
              onChange={(e) => set('fail_reason', e.target.value)}
              required
              multiline
              minRows={2}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ 'data-testid': 'qa-fail-reason' }}
            />
          )}
          {form.applicable && form.result === 'pass' && (
            <TextField
              label="성공 날짜"
              type="date"
              value={form.passed_at}
              onChange={(e) => set('passed_at', e.target.value)}
              size="small"
              sx={{ width: 200 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="담당자"
            value={form.assignee}
            onChange={(e) => set('assignee', e.target.value)}
            size="small"
            sx={{ maxWidth: 280 }}
          />
          <TextField
            label="비고"
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          취소
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving} data-testid="qa-result-submit">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QaResultDialog;
